import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { splitLocale, toSupportedLocale } from '@shared/models/auth';
import { AuthService } from '@tvd/data-access/auth';
import { ConnectivityService } from '@tvd/data-access/connectivity';
import { PaywallRedirector } from '@tvd/data-access/subscription';
import { Storage } from '@tvd/utils/storage';
import { TranslationCheatService } from '@tvd/utils/translation-cheat';
import { map, startWith, switchMap, tap } from 'rxjs';

export const init = () => {
  inject(ConnectivityService); // Starts connectivity monitoring + offline toast
  inject(PaywallRedirector); // Starts paywall redirect watcher (paywalled owners -> billing)

  // Request persistent storage to prevent iOS Safari from evicting CacheStorage
  navigator.storage?.persist?.();
  const translator = inject(TranslateService);
  const auth = inject(AuthService);
  const browserLang = translator.getBrowserCultureLang();
  const storage = inject(Storage);
  inject(TranslationCheatService); // Starts key sequence listener

  // Check localStorage for saved user locale preference first
  // This ensures the language is set immediately on page load
  const savedLocale = storage.get('USER_LOCALE');

  const rawLocale = savedLocale || browserLang || 'de-DE';
  const { language } = splitLocale(rawLocale);

  // Store resolved locale on first visit so the active locale and pipes have it.
  // Canonicalise to a supported locale (en-GB / de-DE) — never en-US.
  if (!savedLocale) {
    storage.set('USER_LOCALE', toSupportedLocale(rawLocale));
  }

  const user$ = toObservable(auth.user.value);

  user$
    .pipe(
      map((user) => {
        if (user?.locale) {
          storage.set('USER_LOCALE', toSupportedLocale(user.locale));
          return splitLocale(user.locale).language;
        }
        return language;
      }),
      startWith(language),
      switchMap((lang) =>
        translator
          .use(lang)
          .pipe(
            tap((content) =>
              translator.setTranslation(lang, content as any, true),
            ),
          ),
      ),
    )
    .subscribe();

  return auth.getRefreshToken();
};
